// src/components/RequireEmployer.tsx
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Skeleton from "./ui/skeleton";
import { Button } from "./ui/button";

/**
 * Guards employer-only app routes. Does NOT create profiles or assign roles.
 * Profile creation belongs on explicit registration submit only.
 */
export default function RequireEmployer({ children }: { children: React.ReactNode }) {
  const { role, profile, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [timeoutError, setTimeoutError] = useState(false);
  const [profileLoadStart] = useState(Date.now());

  // Resolve "Resolving account…" — never hang forever
  useEffect(() => {
    if (loading || !user || profile) {
      setTimeoutError(false);
      return;
    }

    const timer = setTimeout(() => {
      const elapsed = Date.now() - profileLoadStart;
      if (elapsed >= 8000 && !profile) {
        console.error('[RequireEmployer] Profile load timeout after 8s — no auto-create');
        setTimeoutError(true);
      }
    }, 8000);

    return () => clearTimeout(timer);
  }, [loading, user, profile, profileLoadStart]);

  useEffect(() => {
    if (loading) return;

    const profileRole = profile?.role;

    if (import.meta.env.DEV) console.log('[RequireEmployer] Checking access:', {
      roleFromHook: role,
      roleFromProfile: profileRole,
      hasProfile: !!profile,
      userUid: user?.uid,
      userEmail: user?.email,
      orgId: (profile as { orgId?: string } | null)?.orgId,
      currentPath: window.location.pathname,
    });

    if (!user) {
      if (import.meta.env.DEV) console.log('[RequireEmployer] No user, redirecting to /');
      navigate("/", { replace: true });
      return;
    }

    if (!profile && !timeoutError) {
      if (import.meta.env.DEV) console.log('[RequireEmployer] No profile, waiting for profile to load');
      return;
    }

    if (profileRole !== "employer") {
      if (location.pathname === '/employer-registration') {
        if (import.meta.env.DEV) console.log('[RequireEmployer] User on registration page with non-employer role - allowing');
        return;
      }

      console.warn('[RequireEmployer] BLOCKED - User is not an employer:', {
        roleFromHook: role,
        roleFromProfile: profileRole,
        redirectingTo: profileRole === 'emt' ? '/dashboard' : '/employer-registration',
      });

      const redirectPath = profileRole === 'emt' ? '/dashboard' : '/employer-registration';
      navigate(redirectPath, { replace: true });
      return;
    }

    if (import.meta.env.DEV) console.log('[RequireEmployer] Access granted - user is employer');
  }, [profile, role, loading, navigate, user, timeoutError, location.pathname]);

  if (location.pathname.startsWith('/p/')) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-40 w-full mt-4" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!profile) {
    if (timeoutError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="max-w-md w-full bg-[var(--surface-card)] rounded-lg border border-red-200 p-6 shadow-lg">
            <h2 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Account Setup Issue</h2>
            <p className="text-[var(--text-secondary)] mb-4">
              We couldn&apos;t find an employer account for this sign-in. Complete registration to create one: nothing is created until you submit the form.
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => {
                  setTimeoutError(false);
                  window.location.reload();
                }}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white"
              >
                Retry
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/employer-registration', { replace: true })}
                className="w-full"
              >
                Go to Registration
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="p-8 text-center text-[var(--text-secondary)]">
        Resolving account…
      </div>
    );
  }

  const profileRole = profile?.role;
  if (profileRole !== "employer") {
    const redirectPath = profileRole === 'emt' ? '/dashboard' : '/employer-registration';
    if (import.meta.env.DEV) {
      console.log('[GUARD_REDIRECT] RequireEmployer =>', redirectPath, 'from', location.pathname);
      console.trace('[GUARD_REDIRECT_TRACE]');
    }
    return <Navigate to={redirectPath} replace />;
  }

  return <>{children}</>;
}
